import { getCircuitRouteCache, setCircuitRouteCache } from './storage';

/**
 * Cache de la géométrie routière des circuits (Mapbox Directions API).
 *
 * Pourquoi : `InteractiveMap` appelle la Directions API pour tracer le polyligne
 * routier de chaque circuit, recalculé à chaque montage de carte et à chaque
 * bascule aller/retour. La géométrie est pourtant **déterministe** tant que les
 * waypoints (école + arrêts ordonnés) ne changent pas → on la met en cache.
 *
 * Deux niveaux :
 *  - mémoire (Map module-level) : 0 appel sur toutes les navigations d'une session ;
 *  - localStorage (via storage.js) : 0 appel même après rechargement de page.
 *
 * Invalidation : la clé porte un hash des waypoints. Si les arrêts ou l'ordre
 * changent, le hash diffère → cache-miss → recalcul puis re-cache.
 */

// Clé mémoire = `${circuitId}:${hash}` → garde toutes les variantes vues dans la
// session (ex: tracé aller ET retour, qui ont des ordres d'arrêts différents donc
// des hash différents). localStorage ne garde qu'une variante par circuit (bornée).
// Borné par éviction LRU : Map = ordre d'insertion ; on ré-insère à l'accès et on
// évince la plus ancienne au-delà de la capacité → mémoire bornée même sur une
// session longue avec éditions répétées d'arrêts (hash changeants).
const _mem = new Map();
const MEM_CACHE_CAP = 50;

/** Hash stable et compact d'une liste de waypoints [[lng,lat], …]. */
export function hashWaypoints(waypoints) {
  // Coordonnées arrondies à ~1 m pour absorber le bruit float, jointes en string.
  const s = waypoints.map((c) => `${(+c[0]).toFixed(5)},${(+c[1]).toFixed(5)}`).join(';');
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i); // djb2 xor
  }
  return (h >>> 0).toString(36);
}

/**
 * Retourne la géométrie cachée pour `circuitId` si son hash correspond, sinon null.
 * @returns {{ coordinates: number[][], distance: number, duration: number } | null}
 */
export function getCachedCircuitRoute(circuitId, hash) {
  const memKey = `${circuitId}:${hash}`;
  if (_mem.has(memKey)) {
    const data = _mem.get(memKey);
    _mem.delete(memKey);
    _mem.set(memKey, data); // ré-insère en fin → marque comme récemment utilisé (LRU)
    return data;
  }

  const persisted = getCircuitRouteCache(circuitId);
  if (persisted && persisted.hash === hash) {
    memSet(memKey, persisted.data); // réchauffe le cache mémoire
    return persisted.data;
  }
  return null;
}

/** Met en cache (mémoire + localStorage) la géométrie d'un circuit. */
export function setCachedCircuitRoute(circuitId, hash, data) {
  memSet(`${circuitId}:${hash}`, data);
  setCircuitRouteCache(circuitId, hash, data);
}

/** Écrit dans le cache mémoire avec éviction LRU au-delà de la capacité. */
function memSet(memKey, data) {
  _mem.delete(memKey);
  _mem.set(memKey, data);
  if (_mem.size > MEM_CACHE_CAP) {
    _mem.delete(_mem.keys().next().value); // évince l'entrée la plus ancienne
  }
}

/** Vide le cache mémoire (tests). */
export function _clearCircuitRouteMemoryCache() {
  _mem.clear();
}

/** Taille courante du cache mémoire (tests — vérification de la borne LRU). */
export function _circuitRouteMemorySize() {
  return _mem.size;
}
